import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    useTheme,
    Button,
    CircularProgress,
    Alert,
    Stack,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    IconButton,
    Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { collectionPointsService, CollectionPoint, CollectionPointLabelMap, CollectionPointDataElementMap, CollectionPointPurposeMap } from '../../services/collectionpoints-service';

interface NoticeDesignerProps {
    open: boolean;
    onClose: () => void;
    collectionPoint: CollectionPoint | null;
    onSaved?: () => void;
}

interface NoticeConfig {
    titleLabelId?: number;
    subtitleLabelId?: number | null;
    descriptionLabelId?: number | null;
    submitButtonLabelId?: number;
    dataElementOrder?: number[];
    purposeOrder?: number[];
}

const NoticeDesigner: React.FC<NoticeDesignerProps> = ({ open, onClose, collectionPoint, onSaved }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isDPPortal, setIsDPPortal] = useState(false);
    
    // Data states
    const [labels, setLabels] = useState<CollectionPointLabelMap[]>([]);
    const [dataElements, setDataElements] = useState<CollectionPointDataElementMap[]>([]);
    const [purposes, setPurposes] = useState<CollectionPointPurposeMap[]>([]);
    const [existingConfig, setExistingConfig] = useState<NoticeConfig | null>(null);

    // Form state for label selection (using IDs)
    const [formState, setFormState] = useState<{
        titleLabelId: number | '';
        subtitleLabelId: number | '';
        descriptionLabelId: number | '';
        submitButtonLabelId: number | '';
    }>({
        titleLabelId: '',
        subtitleLabelId: '',
        descriptionLabelId: '',
        submitButtonLabelId: '',
    });

    // Validation errors
    const [validationErrors, setValidationErrors] = useState<{
        titleLabelId?: string;
        submitButtonLabelId?: string;
        dataElementOrder?: string;
        purposeOrder?: string;
    }>({});

    // Ordered arrays (by ID)
    const [dataElementOrder, setDataElementOrder] = useState<number[]>([]);
    const [purposeOrder, setPurposeOrder] = useState<number[]>([]);

    // Check if collection point is DP_PORTAL
    useEffect(() => {
        if (collectionPoint) {
            const isDPPortalCP = collectionPoint.CollectionPointIdentifier?.startsWith('DP_PORTAL_') || false;
            setIsDPPortal(isDPPortalCP);
            
            if (isDPPortalCP) {
                setError('This is a system collection point and cannot be configured as a notice form.');
            } else {
                setError(null);
            }
        }
    }, [collectionPoint]);

    // Load data when component opens
    useEffect(() => {
        if (open && collectionPoint && !isDPPortal) {
            loadData();
        } else {
            // Reset states when modal closes
            setLabels([]);
            setDataElements([]);
            setPurposes([]);
            setExistingConfig(null);
            setError(null);
            setFormState({
                titleLabelId: '',
                subtitleLabelId: '',
                descriptionLabelId: '',
                submitButtonLabelId: '',
            });
            setValidationErrors({});
            setDataElementOrder([]);
            setPurposeOrder([]);
        }
    }, [open, collectionPoint, isDPPortal]);

    // Validate form
    const validateForm = (): boolean => {
        const errors: { 
            titleLabelId?: string; 
            submitButtonLabelId?: string;
            dataElementOrder?: string;
            purposeOrder?: string;
        } = {};
        
        if (!formState.titleLabelId) {
            errors.titleLabelId = 'Title label is required';
        }
        
        if (!formState.submitButtonLabelId) {
            errors.submitButtonLabelId = 'Submit button label is required';
        }

        // Validate data element order
        if (dataElements.length > 0) {
            const allElementIds = new Set(dataElements.map(de => de.DataElementId));
            const orderedIds = new Set(dataElementOrder);
            
            // Check if all elements are included
            const missing = Array.from(allElementIds).filter(id => !orderedIds.has(id));
            const extra = Array.from(orderedIds).filter(id => !allElementIds.has(id));
            
            if (missing.length > 0 || extra.length > 0) {
                errors.dataElementOrder = 'All data elements must be included in the order';
            }
            
            // Check for duplicates
            if (dataElementOrder.length !== new Set(dataElementOrder).size) {
                errors.dataElementOrder = 'Duplicate data elements found in order';
            }
        }

        // Validate purpose order
        if (purposes.length > 0) {
            const allPurposeIds = new Set(purposes.map(p => p.PurposeId));
            const orderedIds = new Set(purposeOrder);
            
            // Check if all purposes are included
            const missing = Array.from(allPurposeIds).filter(id => !orderedIds.has(id));
            const extra = Array.from(orderedIds).filter(id => !allPurposeIds.has(id));
            
            if (missing.length > 0 || extra.length > 0) {
                errors.purposeOrder = 'All CONSENT purposes must be included in the order';
            }
            
            // Check for duplicates
            if (purposeOrder.length !== new Set(purposeOrder).size) {
                errors.purposeOrder = 'Duplicate purposes found in order';
            }
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle reordering
    const handleMoveDataElement = (index: number, direction: 'up' | 'down') => {
        const newOrder = [...dataElementOrder];
        if (direction === 'up' && index > 0) {
            [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
        } else if (direction === 'down' && index < newOrder.length - 1) {
            [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        }
        setDataElementOrder(newOrder);
        
        // Clear validation error
        if (validationErrors.dataElementOrder) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.dataElementOrder;
                return newErrors;
            });
        }
    };

    const handleMovePurpose = (index: number, direction: 'up' | 'down') => {
        const newOrder = [...purposeOrder];
        if (direction === 'up' && index > 0) {
            [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
        } else if (direction === 'down' && index < newOrder.length - 1) {
            [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        }
        setPurposeOrder(newOrder);
        
        // Clear validation error
        if (validationErrors.purposeOrder) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.purposeOrder;
                return newErrors;
            });
        }
    };

    // Get ordered data elements and purposes
    const getOrderedDataElements = () => {
        return dataElementOrder
            .map(id => dataElements.find(de => de.DataElementId === id))
            .filter((de): de is CollectionPointDataElementMap => de !== undefined);
    };

    const getOrderedPurposes = () => {
        return purposeOrder
            .map(id => purposes.find(p => p.PurposeId === id))
            .filter((p): p is CollectionPointPurposeMap => p !== undefined);
    };

    // Handle label selection change
    const handleLabelChange = (field: keyof typeof formState, value: string | number) => {
        // Convert string to number for ID fields
        const numValue = value === '' ? '' : Number(value);
        setFormState(prev => ({
            ...prev,
            [field]: numValue,
        }));
        
        // Clear validation error for this field
        if (validationErrors[field as keyof typeof validationErrors]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field as keyof typeof validationErrors];
                return newErrors;
            });
        }
    };

    // Handle save
    const handleSave = async () => {
        if (!collectionPoint) return;

        // Validate form
        if (!validateForm()) {
            setError('Please fix validation errors before saving.');
            return;
        }

        try {
            setSaving(true);
            setError(null);
            setSuccessMessage(null);

            // Build notice config payload
            // Convert empty strings to proper numbers (validation ensures these are set)
            const noticeConfig = {
                titleLabelId: Number(formState.titleLabelId),
                subtitleLabelId: formState.subtitleLabelId === '' ? null : Number(formState.subtitleLabelId),
                descriptionLabelId: formState.descriptionLabelId === '' ? null : Number(formState.descriptionLabelId),
                submitButtonLabelId: Number(formState.submitButtonLabelId),
                dataElementOrder: dataElementOrder,
                purposeOrder: purposeOrder,
            };

            // Call API to save
            await collectionPointsService.updateNoticeConfig(
                collectionPoint.CollectionPointId,
                noticeConfig
            );

            setSuccessMessage('Notice configuration saved successfully!');
            
            // Call onSaved callback to refresh parent list
            if (onSaved) {
                onSaved();
            }

            // Close modal after a short delay to show success message
            setTimeout(() => {
                handleClose();
            }, 1000);

        } catch (err) {
            console.error('Error saving notice configuration:', err);
            setError(err instanceof Error ? err.message : 'Failed to save notice configuration');
        } finally {
            setSaving(false);
        }
    };

    const loadData = async () => {
        if (!collectionPoint) return;

        try {
            setLoading(true);
            setError(null);

            // Load all data in parallel
            const [labelsData, dataElementsData, purposesData, collectionPointData] = await Promise.all([
                collectionPointsService.getMappedLabels(collectionPoint.CollectionPointId),
                collectionPointsService.getMappedDataElements(collectionPoint.CollectionPointId),
                collectionPointsService.getMappedPurposes(collectionPoint.CollectionPointId),
                collectionPointsService.getCollectionPointById(collectionPoint.CollectionPointId),
            ]);

            setLabels(labelsData);
            setDataElements(dataElementsData);
            
            // Filter purposes to only CONSENT lawful ground
            const consentPurposes = purposesData.filter(p => 
                p.lawfulGroundLabel?.toLowerCase() === 'consent'
            );
            setPurposes(consentPurposes);

            // Initialize order arrays
            const defaultDataElementOrder = dataElementsData.map(de => de.DataElementId);
            const defaultPurposeOrder = consentPurposes.map(p => p.PurposeId);

            // Parse existing NoticeConfigJSON if it exists
            if (collectionPointData.NoticeConfigJSON) {
                try {
                    const config = typeof collectionPointData.NoticeConfigJSON === 'string'
                        ? JSON.parse(collectionPointData.NoticeConfigJSON)
                        : collectionPointData.NoticeConfigJSON;
                    setExistingConfig(config);
                    
                    // Pre-populate form state from existing config
                    setFormState({
                        titleLabelId: config.titleLabelId || '',
                        subtitleLabelId: config.subtitleLabelId || '',
                        descriptionLabelId: config.descriptionLabelId || '',
                        submitButtonLabelId: config.submitButtonLabelId || '',
                    });

                    // Pre-populate order from existing config, or use default
                    // Validate that order arrays match current items before using
                    if (config.dataElementOrder && 
                        Array.isArray(config.dataElementOrder) &&
                        config.dataElementOrder.length === defaultDataElementOrder.length &&
                        config.dataElementOrder.every((id: number) => defaultDataElementOrder.includes(id))) {
                        setDataElementOrder(config.dataElementOrder);
                    } else {
                        setDataElementOrder(defaultDataElementOrder);
                    }

                    if (config.purposeOrder && 
                        Array.isArray(config.purposeOrder) &&
                        config.purposeOrder.length === defaultPurposeOrder.length &&
                        config.purposeOrder.every((id: number) => defaultPurposeOrder.includes(id))) {
                        setPurposeOrder(config.purposeOrder);
                    } else {
                        setPurposeOrder(defaultPurposeOrder);
                    }
                } catch (parseError) {
                    console.error('Error parsing NoticeConfigJSON:', parseError);
                    // Use default order on parse error
                    setFormState({
                        titleLabelId: '',
                        subtitleLabelId: '',
                        descriptionLabelId: '',
                        submitButtonLabelId: '',
                    });
                    setDataElementOrder(defaultDataElementOrder);
                    setPurposeOrder(defaultPurposeOrder);
                }
            } else {
                // Reset form state if no existing config
                setFormState({
                    titleLabelId: '',
                    subtitleLabelId: '',
                    descriptionLabelId: '',
                    submitButtonLabelId: '',
                });
                // Use default order
                setDataElementOrder(defaultDataElementOrder);
                setPurposeOrder(defaultPurposeOrder);
            }

        } catch (err) {
            console.error('Error loading notice designer data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading && !saving) {
            onClose();
        }
    };

    const DRAWER_WIDTH = theme.customLayout?.drawerWidth || 240;
    const HEADER_HEIGHT = theme.customLayout?.headerHeight || 64;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="notice-designer-modal-title"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
                    maxWidth: '1200px',
                    maxHeight: '90vh',
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: theme.shadows[24],
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 3,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
                        Notice Designer
                        {collectionPoint && (
                            <Typography component="span" variant="body2" sx={{ ml: 2, color: theme.palette.text.secondary }}>
                                {collectionPoint.Name}
                            </Typography>
                        )}
                    </Typography>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            minWidth: 'auto',
                            p: 1,
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                bgcolor: theme.palette.action.hover,
                            },
                        }}
                    >
                        <CloseIcon />
                    </Button>
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        p: 3,
                    }}
                >
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {successMessage && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : isDPPortal ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This is a system collection point for audit trail purposes and cannot be configured as a notice form.
                        </Alert>
                    ) : (
                        <Stack spacing={3}>
                            {/* Label Selection Section */}
                            <Box>
                                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                    Label Selection
                                </Typography>
                                
                                {labels.length === 0 ? (
                                    <Alert severity="warning">
                                        No labels are mapped to this collection point. Please add labels before configuring the notice form.
                                    </Alert>
                                ) : (
                                    <Stack spacing={2}>
                                        {/* Title Label - Required */}
                                        <FormControl fullWidth size="small" required error={!!validationErrors.titleLabelId}>
                                            <InputLabel id="title-label-select">Title Label</InputLabel>
                                            <Select
                                                labelId="title-label-select"
                                                id="title-label-select-input"
                                                value={formState.titleLabelId}
                                                label="Title Label"
                                                onChange={(e) => handleLabelChange('titleLabelId', e.target.value)}
                                                sx={{
                                                    bgcolor: theme.palette.background.paper,
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: validationErrors.titleLabelId 
                                                            ? theme.palette.error.main 
                                                            : theme.palette.divider,
                                                    },
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {labels.map((label) => (
                                                    <MenuItem key={label.LabelId} value={label.LabelId}>
                                                        {label.LabelKey} - {label.LabelText}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {validationErrors.titleLabelId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                                    {validationErrors.titleLabelId}
                                                </Typography>
                                            )}
                                        </FormControl>

                                        {/* Subtitle Label - Optional */}
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="subtitle-label-select">Subtitle Label (Optional)</InputLabel>
                                            <Select
                                                labelId="subtitle-label-select"
                                                id="subtitle-label-select-input"
                                                value={formState.subtitleLabelId}
                                                label="Subtitle Label (Optional)"
                                                onChange={(e) => handleLabelChange('subtitleLabelId', e.target.value)}
                                                sx={{
                                                    bgcolor: theme.palette.background.paper,
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {labels.map((label) => (
                                                    <MenuItem key={label.LabelId} value={label.LabelId}>
                                                        {label.LabelKey} - {label.LabelText}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Description Label - Optional */}
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="description-label-select">Description Label (Optional)</InputLabel>
                                            <Select
                                                labelId="description-label-select"
                                                id="description-label-select-input"
                                                value={formState.descriptionLabelId}
                                                label="Description Label (Optional)"
                                                onChange={(e) => handleLabelChange('descriptionLabelId', e.target.value)}
                                                sx={{
                                                    bgcolor: theme.palette.background.paper,
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {labels.map((label) => (
                                                    <MenuItem key={label.LabelId} value={label.LabelId}>
                                                        {label.LabelKey} - {label.LabelText}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Submit Button Label - Required */}
                                        <FormControl fullWidth size="small" required error={!!validationErrors.submitButtonLabelId}>
                                            <InputLabel id="submit-button-label-select">Submit Button Label</InputLabel>
                                            <Select
                                                labelId="submit-button-label-select"
                                                id="submit-button-label-select-input"
                                                value={formState.submitButtonLabelId}
                                                label="Submit Button Label"
                                                onChange={(e) => handleLabelChange('submitButtonLabelId', e.target.value)}
                                                sx={{
                                                    bgcolor: theme.palette.background.paper,
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: validationErrors.submitButtonLabelId 
                                                            ? theme.palette.error.main 
                                                            : theme.palette.divider,
                                                    },
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {labels.map((label) => (
                                                    <MenuItem key={label.LabelId} value={label.LabelId}>
                                                        {label.LabelKey} - {label.LabelText}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {validationErrors.submitButtonLabelId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                                    {validationErrors.submitButtonLabelId}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Stack>
                                )}
                            </Box>

                            <Divider />

                            {/* Data Elements Ordering */}
                            <Box>
                                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                    Data Elements Order ({dataElements.length})
                                </Typography>
                                {dataElements.length > 0 ? (
                                    <Stack spacing={1}>
                                        {getOrderedDataElements().map((de, index) => (
                                            <Paper
                                                key={de.DataElementId}
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    bgcolor: theme.palette.background.paper,
                                                }}
                                            >
                                                <Stack direction="row" spacing={0}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMoveDataElement(index, 'up')}
                                                        disabled={index === 0}
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            padding: '2px',
                                                            minWidth: '24px',
                                                            width: '24px',
                                                            height: '24px',
                                                            '&:hover': { bgcolor: theme.palette.action.hover },
                                                            '&:disabled': { opacity: 0.3 },
                                                        }}
                                                    >
                                                        <KeyboardArrowUp fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMoveDataElement(index, 'down')}
                                                        disabled={index === getOrderedDataElements().length - 1}
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            padding: '2px',
                                                            minWidth: '24px',
                                                            width: '24px',
                                                            height: '24px',
                                                            '&:hover': { bgcolor: theme.palette.action.hover },
                                                            '&:disabled': { opacity: 0.3 },
                                                        }}
                                                    >
                                                        <KeyboardArrowDown fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                                        {de.elementName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                        {de.dataType} {de.IsRequired && '(Required)'}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: '30px', textAlign: 'right' }}>
                                                    #{index + 1}
                                                </Typography>
                                            </Paper>
                                        ))}
                                        {validationErrors.dataElementOrder && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                {validationErrors.dataElementOrder}
                                            </Typography>
                                        )}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                                        No data elements mapped to this collection point
                                    </Typography>
                                )}
                            </Box>

                            <Divider />

                            {/* Purposes Ordering */}
                            <Box>
                                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                    CONSENT Purposes Order ({purposes.length})
                                </Typography>
                                {purposes.length > 0 ? (
                                    <Stack spacing={1}>
                                        {getOrderedPurposes().map((p, index) => (
                                            <Paper
                                                key={p.PurposeId}
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    bgcolor: theme.palette.background.paper,
                                                }}
                                            >
                                                <Stack direction="row" spacing={0}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMovePurpose(index, 'up')}
                                                        disabled={index === 0}
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            padding: '2px',
                                                            minWidth: '24px',
                                                            width: '24px',
                                                            height: '24px',
                                                            '&:hover': { bgcolor: theme.palette.action.hover },
                                                            '&:disabled': { opacity: 0.3 },
                                                        }}
                                                    >
                                                        <KeyboardArrowUp fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMovePurpose(index, 'down')}
                                                        disabled={index === getOrderedPurposes().length - 1}
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            padding: '2px',
                                                            minWidth: '24px',
                                                            width: '24px',
                                                            height: '24px',
                                                            '&:hover': { bgcolor: theme.palette.action.hover },
                                                            '&:disabled': { opacity: 0.3 },
                                                        }}
                                                    >
                                                        <KeyboardArrowDown fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                                        {p.purposeName}
                                                    </Typography>
                                                    {p.purposeDescription && (
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                            {p.purposeDescription}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: '30px', textAlign: 'right' }}>
                                                    #{index + 1}
                                                </Typography>
                                            </Paper>
                                        ))}
                                        {validationErrors.purposeOrder && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                {validationErrors.purposeOrder}
                                            </Typography>
                                        )}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                                        No CONSENT purposes mapped to this collection point
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    )}
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    <Button
                        onClick={handleClose}
                        disabled={loading || saving}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={loading || saving || isDPPortal}
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                            '&:disabled': {
                                bgcolor: theme.palette.action.disabledBackground,
                                color: theme.palette.action.disabled,
                            },
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default NoticeDesigner;

